/**
 * Core Type Definitions for co-taskr application
 * These interfaces match the backend Prisma schema and API contracts
 */

// ==================== User Types ====================

export interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  role: UserRole;
  tenantId?: string;
  tenant?: {
    id?: string;
    name: string;
    slug: string;
    status?: string;
    settings?: {
      logoUrl?: string;
      brandColor?: string;
    };
  };
  avatar?: string;
  twoFactorEnabled?: boolean;
  locale?: string;
  timezone?: string;
  dateFormat?: string;
  weeklyCapacityHours?: number;
  createdAt: string;
  updatedAt: string;
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  MEMBER = 'MEMBER',
  GUEST = 'GUEST',
  STAKEHOLDER = 'STAKEHOLDER',
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  organizationName: string;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

// ==================== Project Types ====================

export interface Project {
  id: string;
  name: string;
  key?: string;
  description?: string;
  status: ProjectStatus;
  startDate: string;
  endDate?: string;
  ownerId: string;
  owner?: User;
  teamMembers?: TeamMember[];
  tasks?: Task[];
  wipLimits?: Record<string, number>;
  milestones?: Milestone[];
  createdAt: string;
  updatedAt: string;
}

export enum ProjectStatus {
  PLANNING = 'PLANNING',
  ACTIVE = 'ACTIVE',
  ON_HOLD = 'ON_HOLD',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED',
}

export interface CreateProjectData {
  name: string;
  description?: string;
  startDate: string;
  endDate?: string;
}

// ==================== Task Types ====================

export enum WorkItemType {
  EPIC = 'EPIC',
  FEATURE = 'FEATURE',
  USER_STORY = 'USER_STORY',
  TASK = 'TASK',
  BUG = 'BUG',
}

export enum DependencyType {
  BLOCKS = 'BLOCKS',
  BLOCKED_BY = 'BLOCKED_BY',
  RELATES_TO = 'RELATES_TO',
  DUPLICATES = 'DUPLICATES',
}

export enum MilestoneStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export interface TaskDependency {
  id: string;
  fromTaskId: string;
  toTaskId: string;
  type: DependencyType;
  fromTask?: { id: string; title: string; status: TaskStatus };
  toTask?: { id: string; title: string; status: TaskStatus };
  createdAt: string;
}

export interface Milestone {
  id: string;
  projectId: string;
  project?: Project;
  title: string;
  description?: string;
  dueDate?: string;
  status: MilestoneStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TaskWatcher {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  email: string;
}

export interface Task {
  id: string;
  sequenceNumber?: number;
  title: string;
  description?: string;
  type: WorkItemType;
  status: TaskStatus;
  priority: TaskPriority;
  projectId: string;
  project?: Project;
  sprintId?: string;
  sprint?: { id: string; name: string; goal?: string | null; status?: string; startDate?: string; endDate?: string };
  assigneeId?: string;
  assignee?: User;
  reporterId: string;
  reporter?: User;
  parentId?: string;
  parent?: { id: string; title: string; type: WorkItemType; status?: TaskStatus };
  children?: Task[];
  _count?: { children: number };
  acceptanceCriteria?: string;
  estimatedHours?: number;
  actualHours?: number;
  dueDate?: string;
  tags?: string[];
  attachments?: Attachment[];
  comments?: Comment[];
  dependencies?: Task[];
  dependenciesFrom?: TaskDependency[];
  dependenciesTo?: TaskDependency[];
  subtasks?: Task[];
  parentTaskId?: string;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_REVIEW = 'IN_REVIEW',
  READY_FOR_TESTING = 'READY_FOR_TESTING',
  NEEDS_ATTENTION = 'NEEDS_ATTENTION',
  BLOCKED = 'BLOCKED',
  DONE = 'DONE',
}

export enum TaskPriority {
  URGENT = 'URGENT',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export interface CreateTaskData {
  title: string;
  description?: string;
  type?: WorkItemType;
  status?: TaskStatus;
  priority?: TaskPriority;
  projectId: string;
  assigneeId?: string;
  parentId?: string;
  acceptanceCriteria?: string;
  estimatedHours?: number;
  dueDate?: string;
  tags?: string[];
  parentTaskId?: string;
}

export interface UpdateTaskData {
  title?: string;
  description?: string;
  type?: WorkItemType;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeId?: string;
  parentId?: string | null;
  projectId?: string;
  acceptanceCriteria?: string;
  estimatedHours?: number;
  actualHours?: number;
  dueDate?: string;
  tags?: string[];
  position?: number;
}

// ==================== Team Types ====================

export interface TeamMember {
  id: string;
  userId: string;
  user: User;
  projectId: string;
  project: Project;
  role: TeamRole;
  joinedAt: string;
}

export enum TeamRole {
  LEAD = 'LEAD',
  MEMBER = 'MEMBER',
  CONTRIBUTOR = 'CONTRIBUTOR',
}

// ==================== Sprint Types ====================

export interface Sprint {
  id: string;
  name: string;
  goal?: string;
  startDate: string;
  endDate: string;
  status: SprintStatus;
  projectId: string;
  project?: Project;
  tasks?: Task[];
  createdAt: string;
  updatedAt: string;
}

export enum SprintStatus {
  PLANNING = 'PLANNING',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
}

// ==================== Time Tracking Types ====================

export interface TimeEntry {
  id: string;
  taskId: string;
  task?: Task;
  userId: string;
  user?: User;
  startTime: string;
  endTime?: string;
  duration?: number;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTimeEntryData {
  taskId: string;
  startTime: string;
  endTime?: string;
  description?: string;
}

// ==================== Comment Types ====================

export interface Comment {
  id: string;
  content: string;
  taskId: string;
  task?: Task;
  userId: string;
  user?: User;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCommentData {
  content: string;
  taskId: string;
}

// ==================== Attachment Types ====================

export interface Attachment {
  id: string;
  filename: string;
  url: string;
  mimeType: string;
  size: number;
  taskId: string;
  task?: Task;
  uploadedById: string;
  uploadedBy?: User;
  createdAt: string;
}

// ==================== Report Types ====================

export interface ProjectReport {
  projectId: string;
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  blockedTasks: number;
  todoTasks: number;
  totalEstimatedHours: number;
  totalActualHours: number;
  completionPercentage: number;
  totalLoggedHours: number;
  projectBreakdown: Array<{ projectId: string; statusBreakdown: Record<string, number> }>;
  narrative: string;
}

export interface UserProductivity {
  user: Pick<User, 'id' | 'firstName' | 'lastName' | 'email' | 'role'> | null;
  assignedTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  completionRate: number;
  totalHoursLogged: number;
}

export interface TasksByStatus {
  status: TaskStatus;
  count: number;
  percentage: number;
}

export interface TasksByPriority {
  priority: TaskPriority;
  count: number;
}

// ==================== Utility Types ====================

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ApiError {
  message: string;
  statusCode: number;
  errors?: Record<string, string[]>;
}

export interface FilterOptions {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  type?: WorkItemType[];
  assigneeId?: string[];
  projectId?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ==================== UI State Types ====================

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
}

export interface ModalState {
  isOpen: boolean;
  modalType: string | null;
  modalProps: Record<string, any>;
}

export interface NotificationState {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  timestamp: number;
}

// ==================== Chart Data Types ====================

export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface TimeSeriesData {
  date: string;
  value: number;
}

export interface BurndownData {
  date: string;
  ideal: number;
  actual: number;
}

// ==================== Drag and Drop Types ====================

export interface DragItem {
  id: string;
  type: string;
  index: number;
}

export interface DropResult {
  droppedItem: DragItem;
  targetColumn: string;
  targetIndex: number;
}
